import { styles } from './UserImageAreaSelectionOverlay.style';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import Button from '@photosi/rn-components/buttons/button/Button';
import { productPreviewTestID, userImageAreaTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { UserImageAreaSelectionOverlayBody } from './UserImageAreaSelectionOverlayBody';
import { UserImageAreaModelInstance } from 'src/store/_models/userImageArea';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';

export type UserImageAreaSelectionOverlayProps = {
  isDragging?: boolean;
  index: number;
  userImageArea: UserImageAreaModelInstance;
  onPress: () => Promise<void>;
};

export const UserImageAreaSelectionOverlay = observer(
  ({ onPress, userImageArea, index, isDragging = false }: UserImageAreaSelectionOverlayProps) => {
    const {
      i18n,
      project: { activeProductId },
    } = useStore();
    const hoverAnimationSV = useSharedValue(0);
    const animatedHoverLayerStyles = useAnimatedStyle(() => ({ opacity: hoverAnimationSV.value }));
    const onHoverIn = () => (hoverAnimationSV.value = withTiming(1, { duration: 300, easing: Easing.ease }));
    const onHoverOut = () => (hoverAnimationSV.value = withTiming(0, { duration: 300, easing: Easing.ease }));
    const focusable = activeProductId === userImageArea.productId;

    return (
      <Animated.View style={[styles.workingAreaOverlayContainer, animatedHoverLayerStyles]}>
        <Button
          style={[styles.selectionContainer, isDragging ? styles.cursorGrabbing : styles.cursorGrab]}
          innerStyle={styles.selectionContainerInner}
          onHoverIn={onHoverIn}
          onHoverOut={onHoverOut}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:drag_or_edit`)}
          accessibilityRole="button"
          focusId={productPreviewTestID(
            userImageArea.productId,
            undefined,
            userImageAreaTestID(userImageArea.id, TestIDs.SELECTION, String(index)),
          )}
          focusOrder={index}
          focusGroup={FocusGroups.PRODUCT}
          focusable={focusable}
          onPress={onPress}
        >
          {({ focused, hovered }) => (
            <UserImageAreaSelectionOverlayBody
              focusable={focusable}
              focused={focused}
              hovered={hovered}
              onHoverIn={onHoverIn}
              onHoverOut={onHoverOut}
            />
          )}
        </Button>
      </Animated.View>
    );
  },
);
